/**
 * Adapter layer: Convert between Convex schema and engine types
 * Keeps engine pure and Convex layer thin
 */

import { Card, PotStructure, Pot } from '@ppwr/poker-engine'
import type { Doc } from '../_generated/dataModel'
import type { GameState, EnginePlayer, PlayerState } from '@ppwr/poker-engine'
import { hasPlayerActedThisRound } from './helpers'

/**
 * Normalize pots from Convex format to engine PotStructure (array format)
 * Handles both new array format and legacy object format for migration compatibility
 *
 * @param pots - Pots from Convex game document (union type)
 * @returns Normalized PotStructure (array of Pot)
 */
export function normalizePots(pots: Doc<'games'>['pots']): PotStructure {
  // New format: already an array
  if (Array.isArray(pots)) {
    return pots as PotStructure
  }

  // Legacy format: convert object to array
  const result: Pot[] = []

  // Add main pot first (cumulativeLevel = 0)
  if (pots.mainPot) {
    result.push({
      amount: pots.mainPot.amount,
      eligiblePlayerIds: pots.mainPot.eligiblePlayerIds,
      contributions: pots.mainPot.contributions,
      cumulativeLevel: 0
    })
  }

  // Add side pots with increasing cumulativeLevel
  if (pots.sidePots && pots.sidePots.length > 0) {
    let cumulativeLevel = 0
    for (const sidePot of pots.sidePots) {
      // Calculate cumulativeLevel based on contribution per eligible player
      const perPlayerContribution = sidePot.eligiblePlayerIds.length > 0
        ? sidePot.amount / sidePot.eligiblePlayerIds.length
        : 0
      cumulativeLevel += perPlayerContribution

      result.push({
        amount: sidePot.amount,
        eligiblePlayerIds: sidePot.eligiblePlayerIds,
        contributions: sidePot.contributions,
        cumulativeLevel
      })
    }
  }

  return result
}

/**
 * Convert Convex game + players to engine GameState
 * @param game - Convex game document
 * @param players - Convex player documents
 * @returns Engine-compatible game state
 */
export function toEngineState(game: Doc<'games'>, players: Doc<'players'>[]): GameState {
  return {
    boardState: game.boardState,
    communityCards: game.communityCards as Card[],
    deck: game.deck as Card[],
    pots: normalizePots(game.pots),
    players: players.map((p) => toEnginePlayer(p, game, players)), // Pass all players to derive hasActedThisRound
    currentPlayerId: game.state === 'playing' ? game.currentPlayerId : null,
    button: game.button,
    handNumber: game.handNumber,
    bigBlind: game.bigBlind,
    smallBlind: game.smallBlind
  }
}

/**
 * Convert Convex player to engine player
 * NOTE: Requires game document and all players to derive hasActedThisRound
 * @param player - Convex player document
 * @param game - Convex game document
 * @param allPlayers - All players in the game (needed for hasActedThisRound calculation)
 * @returns Engine-compatible player
 */
export function toEnginePlayer(
  player: Doc<'players'>,
  game: Doc<'games'>,
  allPlayers: Doc<'players'>[]
): EnginePlayer {
  return {
    id: player._id,
    userId: player.userId,
    name: player.name,
    chips: player.chips,
    currentBet: player.currentBet,
    cards: player.cards as Card[],
    state: player.state,
    hasActedThisRound: hasPlayerActedThisRound(player, game, allPlayers), // Derived, not stored!
    seatId: player.seatId,
    position: player.position ?? 'none' // Default 'none' for migration
  }
}

/**
 * Convert engine GameState updates to Convex game updates
 * @param state - Engine game state
 * @returns Partial Convex game document for patching
 */
export function toGameUpdate(state: GameState) {
  return {
    boardState: state.boardState,
    communityCards: state.communityCards,
    deck: state.deck,
    pots: state.pots,
    currentPlayerId: state.currentPlayerId || undefined,
    button: state.button,
    handNumber: state.handNumber
  }
}

/**
 * Convert engine player to Convex player updates
 * NOTE: hasActedThisRound NOT stored - derived from game.currentHandActions!
 * @param player - Engine player
 * @returns Partial Convex player document for patching
 */
export function toPlayerUpdate(player: EnginePlayer) {
  return {
    chips: player.chips,
    currentBet: player.currentBet,
    cards: player.cards,
    state: player.state,
    position: player.position
  }
}
